import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Camera, HardDrive } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { MonitorAlertEventDto, MonitorAlertFullDto } from '@/app/Alert/types';
import { useContextualTimeZone } from '@/components/TimezoneProvider';
import { getDeviceNameByNvrId } from '../utils';
import { Time } from '@/components/ui/time';
import { useAdminAlertContext } from './AdminAlertProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addAlertNote } from '@/lib/api/monitoring/alert';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Kbd } from '@/components/ui/kbd';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { createTagNoteText, createUntagNoteText, formatEventTimeForTag } from '@/lib/tagging';

interface AdminAlertImageViewerProps {
  setShowEventModal: (show: boolean) => void;
  imageRef: React.RefObject<HTMLImageElement>;
}
export function AdminAlertImageViewer({ setShowEventModal, imageRef }: AdminAlertImageViewerProps) {
  const { alert, selectedEvent, goToPreviousEvent, goToNextEvent, currentEventInfo, taggedEventIds, showTagDialog, setShowTagDialog, setStartTimeOverrideMs, startTimeOverrideMs, imageCache, imageCacheStatus } =
    useAdminAlertContext();

  const { timeZone, getUTCOffset } = useContextualTimeZone();
  const [tagComment, setTagComment] = useState('');
  const [isSettingStartTime, setIsSettingStartTime] = useState(false);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Agent';
  const queryClient = useQueryClient();
  const taggingEnabled = useFeatureFlagEnabled(FeatureFlags.Tagging);

  // Check if current event is tagged
  const isCurrentEventTagged = selectedEvent?.id ? taggedEventIds.has(selectedEvent.id) : false;

  // Mutation for adding tag notes to the activity log
  const addTagMutation = useMutation({
    mutationFn: async (noteText: string) => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId) {
        throw new Error('Missing alert data');
      }
      return addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: noteText,
        author: userFullName,
      });
    },
    onSuccess: () => {
      // Invalidate both alert queries to keep all views in sync
      if (alert?.id) {
        queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
        queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      }
      setTagComment('');
      setShowTagDialog(false);
      toast({
        title: 'Event Tagged',
        description: 'Note has been added to the activity log',
        duration: 3000,
      });
    },
    onError: (error) => {
      console.error('Error adding note:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to add note',
        duration: 3000,
      });
    },
  });

  // Mutation for removing tags
  const removeTagMutation = useMutation({
    mutationFn: async () => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId || !selectedEvent?.id) {
        throw new Error('Missing alert data');
      }
      const eventTime = formatEventTimeForTag(selectedEvent.occurred, timeZone);
      return addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: createUntagNoteText({ eventId: selectedEvent.id, eventTime }),
        author: userFullName,
      });
    },
    onSuccess: () => {
      // Invalidate both alert queries to keep all views in sync
      if (alert?.id) {
        queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
        queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      }
      toast({
        title: 'Tag Removed',
        description: 'Tag has been removed from the event',
        duration: 3000,
      });
    },
    onError: (error) => {
      console.error('Error removing tag:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to remove tag',
        duration: 3000,
      });
    },
  });

  const handleTagButtonClick = () => {
    if (!selectedEvent?.id) return;

    if (isCurrentEventTagged) {
      // Untag - remove the tag
      removeTagMutation.mutate();
    } else {
      // Open modal to add tag with comment
      setShowTagDialog(true);
    }
  };

  const handleTagEvent = () => {
    if (!selectedEvent?.id) return;

    const eventTime = formatEventTimeForTag(selectedEvent.occurred, timeZone);
    const imageUrl = selectedEvent.values?.find(
      (value) => value.type === 'SnapshotImage' && value.sourceUrl
    )?.sourceUrl;

    const noteText = createTagNoteText({
      eventId: selectedEvent.id,
      eventTime,
      comment: tagComment,
      imageUrl,
    });

    addTagMutation.mutate(noteText);
  };

  const handleSetStartTime = async () => {
    if (!selectedEvent?.occurred || !alert?.id) return;
    const eventMs = new Date(selectedEvent.occurred).getTime();
    const isAlreadySet = startTimeOverrideMs !== null && eventMs === startTimeOverrideMs;

    setIsSettingStartTime(true);
    setStartTimeOverrideMs(isAlreadySet ? null : eventMs);
    try {
      const overrideNote = isAlreadySet
        ? '[START_TIME_OVERRIDE]NONE'
        : `[START_TIME_OVERRIDE]${new Date(selectedEvent.occurred).toISOString()}`;
      await addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId!,
        projectId: alert.projectId!,
        note: overrideNote,
        author: userFullName,
      });
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      toast({
        title: isAlreadySet ? 'Start time removed' : 'Start time updated',
        description: isAlreadySet ? undefined : 'Incident start time has been set to this event.',
      });
    } catch {
      setStartTimeOverrideMs(isAlreadySet ? startTimeOverrideMs : null);
    } finally {
      setIsSettingStartTime(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      switch (event.key) {
        case 'r':
          event.preventDefault();
          handleSetStartTime();
          break;
        case 't':
          event.preventDefault();
          handleTagButtonClick();
          break;
        case 'g':
          event.preventDefault();
          setShowEventModal(true);
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEvent, alert, isSettingStartTime, startTimeOverrideMs]);

  const [imageDimensions, setImageDimensions] = useState({
    naturalWidth: 0,
    naturalHeight: 0,
    renderedWidth: 0,
    renderedHeight: 0,
    offsetX: 0,
    offsetY: 0,
  });

  // Helper function to parse polygon coordinates from WKT format
  const parsePolygon = useCallback((polygonStr: string): { x: number; y: number }[] => {
    try {
      // Extract all numbers from the polygon string
      const numbers = polygonStr.match(/[-+]?[0-9]*\.?[0-9]+/g);
      if (!numbers) {
        console.error('No numbers found in polygon string:', polygonStr);
        return [];
      }

      // Convert to array of points
      const points: { x: number; y: number }[] = [];
      for (let i = 0; i < numbers.length; i += 2) {
        const x = parseFloat(numbers[i]);
        const y = parseFloat(numbers[i + 1]);
        if (!isNaN(x) && !isNaN(y)) {
          points.push({ x, y });
        }
      }

      return points;
    } catch (e) {
      console.error('Error parsing polygon:', e, polygonStr);
      return [];
    }
  }, []);

  const currentImageUrl = useMemo(
    () => (selectedEvent?.id ? imageCache[selectedEvent.id] : undefined) ?? null,
    [imageCache, selectedEvent]
  );

  // Handle image load to calculate dimensions
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current) return;

    const img = imageRef.current;
    const container = img.parentElement;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    // Don't update if dimensions are invalid
    if (naturalWidth === 0 || naturalHeight === 0) return;

    let renderedWidth = containerWidth;
    let renderedHeight = (containerWidth / naturalWidth) * naturalHeight;

    if (renderedHeight > containerHeight) {
      renderedHeight = containerHeight;
      renderedWidth = (containerHeight / naturalHeight) * naturalWidth;
    }

    const offsetX = (containerWidth - renderedWidth) / 2;
    const offsetY = (containerHeight - renderedHeight) / 2;

    setImageDimensions({
      naturalWidth,
      naturalHeight,
      renderedWidth,
      renderedHeight,
      offsetX,
      offsetY,
    });
  }, []);

  // EventOverlays component to display bounding boxes and activation zones
  const EventOverlays = ({
    event,
    imageDimensions,
    showBoundingBoxes,
    showActivationZone,
  }: {
    event: MonitorAlertEventDto;
    imageDimensions: {
      naturalWidth: number;
      naturalHeight: number;
      renderedWidth: number;
      renderedHeight: number;
      offsetX: number;
      offsetY: number;
    };
    showBoundingBoxes: boolean;
    showActivationZone: boolean;
  }) => {
    if (!imageDimensions.renderedWidth || !imageDimensions.renderedHeight) return null;

    const boundingBoxes =
      event.values
        ?.filter((v) => v.type === 'PersonSeen' || v.type === 'VehicleSeen')
        .map((v) => ({
          type: v.type,
          coordinates: parsePolygon(v.txtValue || ''),
        })) || [];

    return (
      <div className="pointer-events-none absolute inset-0 left-0 top-0">
        <svg
          className="absolute left-0 top-0 h-full w-full"
          style={{
            transform: `translate(${imageDimensions.offsetX}px, ${imageDimensions.offsetY}px)`,
          }}
        >
          {/* Activation Zone */}
          {showActivationZone &&
            event.activationZone &&
            (() => {
              const coordsMatch = event.activationZone.match(/\(\((.*?)\)\)/);
              if (!coordsMatch) return null;

              const points = parsePolygon(coordsMatch[1]);
              if (points.length === 0) return null;

              // Scale points to match image dimensions
              const scaledPoints = points.map((point) => ({
                x: point.x * imageDimensions.renderedWidth,
                y: point.y * imageDimensions.renderedHeight,
              }));

              const pathData =
                scaledPoints
                  .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                  .join(' ') + ' Z';

              return (
                <>
                  <defs>
                    <mask id="activationZoneMask">
                      <rect width="100%" height="100%" fill="white" />
                      <path d={pathData} fill="black" />
                    </mask>
                  </defs>
                  <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0)"
                    mask="url(#activationZoneMask)"
                  />
                  <path
                    d={pathData}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.7)"
                    strokeWidth={2}
                  />
                </>
              );
            })()}

          {/* Bounding Boxes */}
          {showBoundingBoxes &&
            boundingBoxes.map((box, index) => {
              if (box.coordinates.length === 0) return null;

              const scaledPoints = box.coordinates.map((point) => ({
                x: point.x * imageDimensions.renderedWidth,
                y: point.y * imageDimensions.renderedHeight,
              }));

              const pathData =
                scaledPoints
                  .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                  .join(' ') + ' Z';

              // Use same colors as AlertTimelineV2: red for person, blue for vehicle
              const color = box.type === 'PersonSeen' ? '#FF6666' : '#66B2FF';
              const fillColor =
                box.type === 'PersonSeen' ? 'rgba(255, 102, 102, 0.2)' : 'rgba(102, 178, 255, 0.2)';

              return (
                <g key={index}>
                  <path d={pathData} fill={fillColor} stroke={color} strokeWidth={1} />
                  <text
                    x={scaledPoints[0]?.x || 0}
                    y={(scaledPoints[0]?.y || 0) - 5}
                    fill={color}
                    fontSize="12"
                    fontWeight="bold"
                    style={{
                      textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
                    }}
                  >
                    {box.type === 'PersonSeen' ? 'Person' : 'Vehicle'}
                  </text>
                </g>
              );
            })}
        </svg>
      </div>
    );
  };

  return (
    <div data-onboarding="admin-alert-image-viewer">
      <div className="flex h-[56px] items-center justify-between border-b">
        <div className="flex items-center gap-3 px-3">
        <HardDrive className="h-6 w-6" />
          <div className=" text-sm">
       
            <p className='font-semibold'>
              {selectedEvent?.nvrId ? (
                <Link to={`/devices/${selectedEvent.nvrId}/edit`} className='text-lime'>
                  {getDeviceNameByNvrId(selectedEvent?.videoProcessorId, alert)}
                </Link>
              ) : (
                <span>{getDeviceNameByNvrId(selectedEvent?.videoProcessorId, alert)}</span>
              )}
       
       {` / `}
              {selectedEvent?.nvrId && selectedEvent?.videoProcessorIdentifier ? (
                <Link
                  to={`/devices/${selectedEvent.nvrId}/camera/${selectedEvent.videoProcessorIdentifier}`}
                  className='text-lime'>
                  {selectedEvent?.nvrDeviceName || 'Unknown Camera'}
                </Link>
              ) : (
                <span>{selectedEvent?.nvrDeviceName || 'Unknown Camera'}</span>
              )}
       </p><p className='text-xs text-gray-400'>     <Time date={selectedEvent?.occurred} fallback="No event selected" /></p>
          </div>
        </div><div className='flex items-center  h-full'>
        </div>
      </div>
      <div className="relative h-full max-h-[calc(100vh-217px)] w-full bg-black">
        {currentImageUrl == null ? (
          <div className="flex h-full w-full items-center justify-center text-gray-500">
            {selectedEvent?.id && imageCacheStatus[selectedEvent.id] === 'loading' ? (
              <Spinner size="large" className="text-gray-500" />
            ) : (
              'No Image'
            )}
          </div>
        ) : (
          <>
            <img
              ref={imageRef}
              src={currentImageUrl || undefined}
              alt="Main image"
              className="h-full w-full object-contain"
              onLoad={handleImageLoad}
            />
            {selectedEvent && (
              <EventOverlays
                event={selectedEvent}
                imageDimensions={imageDimensions}
                showBoundingBoxes={true}
                showActivationZone={true}
              />
            )}
          </>
        )}
      </div>
      <div
        data-onboarding="admin-alert-navigation"
        className="flex h-12 items-center justify-center gap-2 border-b border-t"
      >
        <Button
          hotkey={{ keyboard: 'arrowleft' }}
          onClick={goToPreviousEvent}
          className="rounded-none border-none"
          variant="outline"
        ></Button>
        <p>
          Event {currentEventInfo.current} of {currentEventInfo.total}
        </p>
        <Button
          hotkey={{ keyboard: 'arrowright' }}
          onClick={goToNextEvent}
          className="rounded-none border-none"
          variant="outline"
        ></Button>
        <div className="hidden">
          <Button hotkey={{ keyboard: 'arrowup' }} onClick={goToPreviousEvent}>
            Previous Event
          </Button>

          <Button hotkey={{ keyboard: 'arrowdown' }} onClick={goToNextEvent}>
            Next Event
          </Button>
        </div>
      </div>

      {/* Tag Event Dialog */}
      {taggingEnabled && (
        <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Tag Event</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 p-6">
              <div className="text-sm text-gray-400">
                <p><span className="text-white font-medium">Camera:</span> {selectedEvent?.nvrDeviceName || 'Unknown Camera'}</p>
                <p><span className="text-white font-medium">Event Time:</span> <Time date={selectedEvent?.occurred} fallback="N/A" /></p>
                <p><span className="text-white font-medium">Tagged By:</span> {userFullName}</p>
              </div>
              <Textarea
                value={tagComment}
                onChange={(e) => setTagComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !addTagMutation.isPending) {
                    e.preventDefault();
                    handleTagEvent();
                  }
                }}
                placeholder="Add a comment (optional)..."
                className="h-24 resize-none border-gray-800 bg-[#161616] text-sm text-white placeholder:text-gray-600 focus:border-blue-600"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowTagDialog(false);
                  setTagComment('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTagEvent}
                disabled={addTagMutation.isPending}
              >
                {addTagMutation.isPending ? 'Saving...' : 'Tag Event'} <Kbd size="sm">ctrl</Kbd> + <Kbd size="sm">enter</Kbd>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
